import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { CapturedWebsitesSearchForm } from "./captured-websites-search-form";
import { CapturedWebsitesTable } from "./captured-websites-table";
import { CapturedWebsiteSummarySchema } from "@/domains/openapi/types";
import { Button } from "@/components/button";

export const CapturedWebsitesRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [query, setQuery] = useState<string>(initialQuery);

  useEffect(() => {
    setQuery(searchParams.get("query") ?? "");
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["captured-websites-search", query],
    queryFn: () =>
      api.post("/internal/v1/captured-websites/search", {
        body: { query_string: query || undefined, limit: pageSize },
      }),
    enabled: query.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleView = (id: string) => {
    navigate(atlasRoutes.capturedWebsitesView({ params: { capturedWebsiteId: id } }).path);
  };

  const handleSearch = (q: string) => {
    if (q) searchParams.set("query", q);
    else searchParams.delete("query");
    setSearchParams(searchParams);
  };

  const tableData = useMemo<CapturedWebsiteSummarySchema[]>(
    () => data?.data?.results ?? [],
    [data]
  );

  return (
    <Container>
      <FlexHeader>
        <div>
          <h1>Captured Websites</h1>
          <DescriptionText>Search by URL or title.</DescriptionText>
        </div>
        <Button onClick={() => navigate(atlasRoutes.capturedWebsitesCreate({}).path)}>
          Create Captured Website
        </Button>
      </FlexHeader>
      <CapturedWebsitesSearchForm initialValue={query} onSearch={handleSearch} />
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching captured websites: {error.message}</div>}
      {tableData.length > 0 ? (
        <CapturedWebsitesTable
          data={tableData}
          onViewCapturedWebsite={handleView}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && query && <div>No captured websites found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const FlexHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;
